import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNotificationStore } from '@/store/notification.store'
import { openRealtimeSocket } from '@/services/realtime/socket'
import { applySalesLeadNotification } from '@/modules/sales/utils/sales-lead-realtime'
import { applyTaskNotification } from '@/modules/tasks/utils/task-realtime'
import { taskKeys } from '@/modules/tasks/hooks/useTasks'
import { applyChatRealtime } from '@/modules/messenger/utils/chat-realtime'
import { messengerKeys } from '@/modules/messenger/hooks/useMessenger'
import { siteInboxKeys } from '@/modules/sales/hooks/useSiteInbox'
import type {
  NotificationItem,
  NotificationSyncEvent,
} from '@/modules/notifications/services/notification.service'

/** The only chat.* types that went through NotificationService.notify
 * (persisted, bell-worthy, push/FCM-eligible) — every other chat.* type
 * (typing, receipt/reaction/member_updated, message_deleted) is an
 * ephemeral publishRealtime() push and must never reach the bell store. */
const CHAT_NOTIFICATION_TYPES = new Set(['chat.message_created', 'chat.member_added', 'chat.mention'])

/**
 * Mount exactly once, in the authenticated app shell (AppLayout). Opens the
 * app's one WebSocket for the session (see services/realtime/socket.ts —
 * every live feature rides this same connection, not a module-specific
 * socket), reconnects with backoff on drop, and closes automatically when
 * the shell unmounts (logout / 401).
 */
export function useNotificationRealtime() {
  const queryClient = useQueryClient()

  useEffect(() => {
    const store = () => useNotificationStore.getState()
    // Banners for things already read/deleted elsewhere leave the phone shade on open.
    void store().fetchInbox().then(() => store().syncShade())

    // Back from background: tidy the shade. Going away: send deletes still in their Undo window.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void store().syncShade()
      else store().flushPendingDeletes()
    }
    document.addEventListener('visibilitychange', onVisibility)

    const close = openRealtimeSocket({
      onConnect: ({ reconnect }) => {
        if (!reconnect) return
        void useNotificationStore.getState().backfillUnread()
        // task.receipt_updated / task.reaction_updated are deliberately
        // never persisted (no Notification row), so unlike everything else
        // on this stream they can't be backfilled — a receipt or reaction
        // that changed while this connection was down (dropped mid-drag on
        // mobile, brief network blip) is lost for good unless whatever
        // Discussion is currently open resyncs itself right here, on
        // reconnect.
        void queryClient.invalidateQueries({ queryKey: [...taskKeys.all, 'comments'] })
        // Same reasoning for Messenger's own ephemeral types (receipts,
        // reactions, member_updated) — chat.typing has nothing to resync
        // (it's momentary by design, not a state to catch up on). Prefix
        // match (no chatId) invalidates every currently-mounted thread.
        void queryClient.invalidateQueries({ queryKey: [...messengerKeys.all, 'messages'] })
        void queryClient.invalidateQueries({ queryKey: messengerKeys.chats() })
      },
      onEvent: ({ type, payload }) => {
        // Another device/tab read, deleted or cleared — ephemeral, never a bell row.
        if (type === 'notification.sync') {
          store().applySync(payload as NotificationSyncEvent)
          return
        }
        if (type.startsWith('chat.')) {
          // Two of these (message_created, member_added) are real,
          // persisted Notification rows — same bell/push treatment as any
          // other module. Every other chat.* type is an ephemeral
          // publishRealtime() push (see messengerService.js) and only ever
          // needs the messenger cache to resync — applyChatRealtime makes
          // that split itself, same shape as the task.* handling below.
          if (CHAT_NOTIFICATION_TYPES.has(type)) {
            useNotificationStore.getState().receiveLive(payload as NotificationItem)
          }
          applyChatRealtime(queryClient, type, payload)
          return
        }

        const notification = payload as NotificationItem
        // Synthetic, never-persisted signals (see taskService.js's
        // notifyReceiptUpdate / notifyReactionUpdate) — a live nudge for
        // Discussion ticks/mic badges/reaction chips only, never a real
        // Notification row (no FCM either, so a 👍 never pages anyone's
        // phone), so it must never reach the bell/inbox store (no id/readAt
        // of its own to show).
        if (notification.type === 'task.receipt_updated' || notification.type === 'task.reaction_updated') {
          applyTaskNotification(queryClient, notification)
          return
        }
        useNotificationStore.getState().receiveLive(notification)
        if (notification.entityType === 'sales_lead') {
          applySalesLeadNotification(queryClient, notification)
        } else if (notification.type.startsWith('site_enquiry.')) {
          void queryClient.invalidateQueries({ queryKey: siteInboxKeys.all })
        } else if (
          notification.entityType === 'task' ||
          notification.type === 'task.overdue_digest'
        ) {
          applyTaskNotification(queryClient, notification)
        }
      },
    })

    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      close()
      store().reset()
    }
  }, [queryClient])
}
